/*
 * @Description : 本地缓存
 * @Author      : pishengjun
 * @Date        : 2022-06-08 14:47:16
 */

import Crypto from "@/utils/crypto";

// 默认缓存期限为0.5天
const DEFAULT_CACHE_TIME = 60 * 60 * 24 * 0.5;
// 判断是否生产环境（如是则使用Crypto加密）
const isProduction = import.meta.env.NODE_ENV != "development";
// const isProduction = true;

/**
 * 创建本地缓存对象
 * @param {string=} prefixKey -
 * @param {Object} [storage=localStorage] - sessionStorage | localStorage
 */
export const createStorage = ({ prefixKey = "", storage = localStorage } = {}) => {
  /**
   * 本地缓存类
   * @class Storage
   */
  const Storage = class {
    private storage = storage;
    private prefixKey?: string = prefixKey;

    private getKey(key: string) {
      // return `${this.prefixKey}${key}`.toLowerCase()
      return `${this.prefixKey}${key}`;
    }

    /**
     * @description 设置缓存
     * @param {string} key 缓存键
     * @param {*} value 缓存值
     * @param expire
     */
    set(key: string, value: any, expire: number | null = DEFAULT_CACHE_TIME) {
      // 获取当前时间的秒级时间戳
      const timestampInSeconds = Math.floor(new Date().getTime() / 1000);
      let stringData = JSON.stringify({
        value: value,
        expire: expire !== null ? timestampInSeconds + expire : null,
      });
      stringData = isProduction ? Crypto.encrypt(stringData) : stringData;
      this.storage.setItem(this.getKey(key), stringData);
    }
    /**
     * 批量设置缓存
     * @param {Record<string, any>} items 键值对对象
     * @param {number | null} expire 过期时间
     */
    setMultiple(items: Record<string, any>, expire: number | null = DEFAULT_CACHE_TIME) {
      for (const [key, value] of Object.entries(items)) {
        this.set(key, value, expire);
      }
    }

    /**
     * 读取缓存
     * @param {string} key 缓存键
     * @param {*=} def 默认值
     */
    get(key: string, def: any = null) {
      const item = this.storage.getItem(this.getKey(key));
      if (item) {
        try {
          // 获取当前时间的秒级时间戳
          const timestampInSeconds = Math.floor(new Date().getTime() / 1000);
          let data: any = isProduction ? Crypto.decrypt(item) : item;
          data = JSON.parse(data) || {};
          const { value, expire } = data;
          // 在有效期内直接返回
          if (expire === null || expire >= timestampInSeconds) {
            return value;
          }
          this.remove(this.getKey(key));
        } catch (e) {
          return def;
        }
      }
      return def;
    }

    /**
     * 从缓存删除某项
     * @param {string} key
     */
    remove(key: string) {
      this.storage.removeItem(this.getKey(key));
    }

    /**
     * 从缓存删除某项key开头的缓存
     * @param {string} key
     */
    removeKeyStartsWith(keyStartsWith: string) {
      const keys = Object.keys(this.storage) || [];
      keys.forEach((key) => {
        if (key.startsWith(keyStartsWith)) {
          this.storage.removeItem(this.getKey(key));
        }
      });
    }

    /**
     * 清除存储中的指定键值对。
     *
     */
    clear(): void {
      this.storage.clear();
    }
    /**
     * 清除存储中除指定键之外的所有键值对。
     *
     * @param includeKeys 需要清除的键数组。
     * 如果提供了 includeKeys 参数，那么只清除在此数组中的键值对，
     * 其他的键值对会保留。
     */
    clearIncludeKeys(includeKeys: string[] = []): void {
      for (let i = 0; i < this.storage.length; i++) {
        const key = this.storage.key(i)?.toString() ?? "";
        if (key === this.getKey(includeKeys[i])) {
          this.storage.removeItem(key);
        }
      }
    }

    /**
     * 清除存储中除指定键之外的所有键值对。
     *
     * @param excludeKeys 需要保留在存储中的键数组。
     * 如果提供了 excludeKeys 参数，那么只保留在此数组中的键值对，
     * 其他的键值对都会被清除。
     */
    clearExcludeKeys(excludeKeys: string[]): void {
      for (let i = 0; i < this.storage.length; i++) {
        const key = this.storage.key(i)?.toString() ?? "";
        if (key !== this.getKey(excludeKeys[i])) {
          this.storage.removeItem(key);
        }
      }
    }

    /**
     * 设置cookie
     * @param {string} name cookie 名称
     * @param {*} value cookie 值
     * @param {number=} expire 过期时间
     * 如果过期时间为设置，默认关闭浏览器自动删除
     * @example
     */
    setCookie(name: string, value: any, expire: number | null = DEFAULT_CACHE_TIME) {
      document.cookie = `${this.getKey(name)}=${value}; Max-Age=${expire}`;
    }

    /**
     * 根据名字获取cookie值
     * @param name
     */
    getCookie(name: string): string {
      const cookieArr = document.cookie.split("; ");
      for (let i = 0, length = cookieArr.length; i < length; i++) {
        const kv = cookieArr[i].split("=");
        if (kv[0] === this.getKey(name)) {
          return kv[1];
        }
      }
      return "";
    }

    /**
     * 根据名字删除指定的cookie
     * @param {string} key
     */
    removeCookie(key: string) {
      this.setCookie(key, 1, -1);
    }

    /**
     * 清空cookie，使所有cookie失效
     */
    clearCookie(): void {
      try {
        const keys = document.cookie.split(";") || [];
        for (let i = 0; i < keys.length; i++) {
          const key = keys[i];
          const name = key.trim().split("=")[0];
          try {
            localStorage.removeItem(name);
            sessionStorage.removeItem(name);
          } catch (e) {
            console.error(`移除localStorage或sessionStorage时出错: ${e}`);
          }
        }
      } catch (e) {
        console.error(`获取或分割cookie时出错: ${e}`);
      }
    }
  };
  return new Storage();
};

export const storage = createStorage();
export const session_storage = createStorage({ storage: sessionStorage });

export default Storage;
